import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { makeGeocoder } from '../../helpers/pitchGeocoder';

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Shared marker glyph paths (24x24 viewBox). Star marks grant winners; the
// pin is the default. Reused by the divIcon HTML and the tooltip label.
const STAR_PATH = 'M12 2.5l2.83 5.9 6.47.86-4.73 4.5 1.19 6.4L12 17.1l-5.76 3.06 1.19-6.4-4.73-4.5 6.47-.86L12 2.5z';
const PIN_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';

// Custom marker icon component. `approximate` pins (placed at a chapter center
// because the zip has no precise coordinates yet) render dimmed.
const createCustomIcon = (isWinner, approximate = false) => {
  const glyph = isWinner
    ? `<svg width="24" height="24" viewBox="0 0 24 24" style="display: block;">
        <path d="${STAR_PATH}" fill="var(--mb-butter)" stroke="var(--mb-ink)" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>`
    : `<svg width="24" height="24" viewBox="0 0 24 24" style="display: block;">
        <path d="${PIN_PATH}" fill="var(--mb-magenta)" stroke="var(--mb-ink)" stroke-width="1.5" stroke-linejoin="round"/>
        <circle cx="12" cy="9" r="2.5" fill="var(--mb-chalk)"/>
      </svg>`;
  return L.divIcon({
    html: `<div style="
      width: 24px;
      height: 24px;
      cursor: pointer;
      opacity: ${approximate ? 0.6 : 1};
      transform: translate(-12px, -12px);
    ">${glyph}</div>`,
    iconSize: [24, 24],
    className: 'custom-pitch-marker',
    iconAnchor: [12, 12],
  });
};

// Component to handle map bounds updates
function MapBoundsHandler({ onBoundsChange }) {
  const map = useMap();
  
  useEffect(() => {
    const handleMoveEnd = () => {
      const bounds = map.getBounds();
      onBoundsChange(bounds);
    };
    
    map.on('moveend', handleMoveEnd);
    handleMoveEnd(); // Initial bounds
    
    return () => {
      map.off('moveend', handleMoveEnd);
    };
  }, [map, onBoundsChange]);
  
  return null;
}

// Component to auto-fit map to markers
function AutoFitBounds({ markers }) {
  const map = useMap();
  
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, markers]);
  
  return null;
}

export default function PitchMap({ pitches = [], currentUserChapter, onPitchClick }) {
  const [mapBounds, setMapBounds] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterQuarter, setFilterQuarter] = useState('');
  const [showWinnersOnly, setShowWinnersOnly] = useState(false);
  const [zipcodeData, setZipcodeData] = useState(null);
  const [pitchesWithCoords, setPitchesWithCoords] = useState([]);

  // Load zipcode data
  useEffect(() => {
    fetch('/data/zipcodes.json')
      .then(res => res.json())
      .then(data => {
        setZipcodeData(data);
      })
      .catch(err => {
        console.error('PitchMap: Failed to load zipcode data:', err);
      });
  }, []);
  
  // Geocode pitches once zipcode data is loaded. The shared geocoder resolves
  // every chapter (precise zip where available, chapter-center fallback
  // otherwise), so no chapter silently drops off the map.
  useEffect(() => {
    if (!zipcodeData) return;

    const geocode = makeGeocoder(zipcodeData);
    const located = pitches.map((pitch) => {
      if (typeof pitch.lat === 'number' && typeof pitch.lng === 'number') {
        return pitch;
      }
      const coords = geocode(pitch);
      return coords
        ? { ...pitch, lat: coords.lat, lng: coords.lng, approximate: coords.approximate }
        : pitch;
    });

    setPitchesWithCoords(located);
  }, [pitches, zipcodeData]);

  // Get unique chapters and quarters for filters
  const chapters = useMemo(() => {
    const uniqueChapters = [...new Set(pitchesWithCoords.map(p => p.chapter).filter(Boolean))];
    return uniqueChapters.sort();
  }, [pitchesWithCoords]);

  const quarters = useMemo(() => {
    const uniqueQuarters = [...new Set(pitchesWithCoords.map(p => p.quarter).filter(Boolean))];
    return uniqueQuarters.sort((a, b) => {
      const [aQ, aY] = a.split(' ');
      const [bQ, bY] = b.split(' ');
      if (bY !== aY) return parseInt(bY) - parseInt(aY);
      return parseInt(aQ.substring(1)) - parseInt(bQ.substring(1));
    });
  }, [pitchesWithCoords]);

  // Filter pitches based on criteria
  const filteredPitches = useMemo(() => {
    return pitchesWithCoords.filter(pitch => {
      // Must have coordinates
      if (!pitch.lat || !pitch.lng) return false;
      
      // Quarter filter
      if (filterQuarter && pitch.quarter !== filterQuarter) return false;
      
      // Winners only filter
      if (showWinnersOnly && !pitch.isWinner) return false;
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          pitch.businessName?.toLowerCase().includes(search) ||
          pitch.founderName?.toLowerCase().includes(search) ||
          pitch.city?.toLowerCase().includes(search)
        );
      }
      
      return true;
    });
  }, [pitchesWithCoords, filterQuarter, showWinnersOnly, searchTerm]);

  // Count stats
  const stats = useMemo(() => {
    const visible = filteredPitches.length;
    const winners = filteredPitches.filter(p => p.isWinner).length;
    return { visible, winners };
  }, [filteredPitches]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar — search, quarter filter, winners-only */}
      <div className="win95-toolbar" role="group" aria-label="Filter map" style={{ flexWrap: 'wrap' }}>
        <input
          type="search"
          className="win95-field"
          placeholder="Search business or founder…"
          aria-label="Search business or founder"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <div className="win95-toolbar__sep" aria-hidden="true" />

        {/* Quarter Filter */}
        <div className="win95-select">
          <select
            className="win95-select__native"
            aria-label="Filter by quarter"
            value={filterQuarter}
            onChange={(e) => setFilterQuarter(e.target.value)}
          >
            <option value="">All quarters</option>
            {quarters.map(quarter => (
              <option key={quarter} value={quarter}>{quarter}</option>
            ))}
          </select>
          <span className="win95-select__arrow" aria-hidden="true" />
        </div>

        {/* Winners Only Toggle */}
        <label className="win95-check">
          <input
            type="checkbox"
            checked={showWinnersOnly}
            onChange={() => setShowWinnersOnly(!showWinnersOnly)}
          />
          Winners only
        </label>
      </div>

      {/* Map in a sunken chrome well — Leaflet internals untouched */}
      <div style={{
        flex: 1,
        position: 'relative',
        minHeight: 0,
        border: '2px solid',
        borderColor: 'var(--bevel-sunken)'
      }}>
        <MapContainer
          center={[39.8283, -98.5795]} // Center of USA
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapBoundsHandler onBoundsChange={setMapBounds} />
          <AutoFitBounds markers={filteredPitches} />
          
          {filteredPitches.map((pitch) => (
            <Marker
              key={pitch.id}
              position={[pitch.lat, pitch.lng]}
              icon={createCustomIcon(pitch.isWinner, pitch.approximate)}
              eventHandlers={{
                click: () => onPitchClick && onPitchClick(pitch)
              }}
            >
              <Tooltip>
                <div style={{ textAlign: 'center' }}>
                  <strong>{pitch.businessName || 'Unknown Business'}</strong><br />
                  by {pitch.founderName || 'Unknown Founder'}<br />
                  {pitch.city && pitch.state ? `${pitch.city}, ${pitch.state}` : pitch.chapter}<br />
                  <span style={{ fontSize: '12px', color: 'var(--mb-ink-60)' }}>
                    {pitch.quarter} • {pitch.chapter}
                  </span>
                  {pitch.isWinner && (
                    <div style={{
                      marginTop: '4px',
                      color: 'var(--green-dark)',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                        <path d={STAR_PATH} fill="currentColor" />
                      </svg>
                      Grant Winner
                    </div>
                  )}
                </div>
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Status bar — counts + geocoder state */}
      <div className="win95-statusbar">
        <div className="win95-statusbar__pane win95-statusbar__pane--grow">
          {zipcodeData ? 'Ready' : 'Geocoding addresses…'}
        </div>
        <div className="win95-statusbar__pane win95-statusbar__pane--data">
          {stats.visible} pitches
        </div>
        <div className="win95-statusbar__pane win95-statusbar__pane--data">
          {stats.winners} winners
        </div>
      </div>
    </div>
  );
}